<template>
  <div class="default-layout">
    <div v-if="!isHideHeader" class="header-container">
      <TopLayoutBanner v-if="isShowTopBanner" />
      <TopLayoutSubscribeBanner v-if="isShowTopSubscribeBanner && !isShowTopBanner" />
      <HeaderLayout @open-side-menu="openSideMenu" @open-geo-modal="openGeoModal" />
    </div>

    <main>
      <slot />
    </main>

    <MenuMobileBottom
      v-if="!isHideFooter"
      @open-side-menu="openSideMenu"
      @open-ecosystem="openEcosystemModal"
    />
    <AsyncFooter v-if="!isHideFooter" />

    <client-only>
      <AsyncMenuSide :is-open="isOpenSideMenu" @on-close="closeSideMenu" />
      <AsyncGeolocationModal
        v-model="isOpenGeoModal"
        @on-agree="closeGeoModal"
        @on-disagree="openChangeRegionModal"
      />
      <AsyncRegionsModal />
      <AsyncCookie />
      <AsyncEcosystemModal v-model="isOpenEcosystemModal" />
      <AsyncAuthModal />
    </client-only>
  </div>
</template>

<script lang="ts" setup>
  import HeaderLayout from '~/components/layouts/header/HeaderLayout.vue'
  import TopLayoutBanner from '~/components/banners/TopLayoutBanner.vue'
  import TopLayoutSubscribeBanner from '~/components/banners/TopLayoutSubscribeBanner.vue'
  import MenuMobileBottom from '~/components/layouts/menu/MenuMobileBottom.vue'
  import { provideGlobalSearch } from '~/composables/global-search/use-global-search'
  import { useRegionModal } from '~/composables/regions/use-region-modal'
  import { useRoute } from 'vue-router'
  import { useUserStore } from '~/store/user'
  import { useRegions } from '~/composables/regions/use-regions'

  const AsyncFooter = defineLazyHydrationComponent(
    'visible',
    () => import('~/components/layouts/footer/FooterLayout.vue')
  )
  const AsyncMenuSide = defineAsyncComponent(() => import('~/components/layouts/menu/MenuSide.vue'))
  const AsyncGeolocationModal = defineAsyncComponent(
    () => import('~/components/layouts/modals/GeolocationModal.vue')
  )
  const AsyncRegionsModal = defineAsyncComponent(
    () => import('~/components/layouts/modals/RegionsModal.vue')
  )
  const AsyncEcosystemModal = defineAsyncComponent(
    () => import('~/components/layouts/modals/EcosystemModal.vue')
  )
  const AsyncAuthModal = defineAsyncComponent(
    () => import('~/components/layouts/modals/AuthModal.vue')
  )
  const AsyncCookie = defineAsyncComponent(
    () => import('~/components/layouts/cookie/CookieLayout.vue')
  )

  defineProps<{
    isHideHeader?: boolean
    isHideFooter?: boolean
  }>()

  provideGlobalSearch()

  const { loadRegions } = useRegions()
  const route = useRoute()
  const { isAuthenticated, hasRole } = useUserStore()
  const { openRegionModal } = useRegionModal()
  const isOpenSideMenu = ref<boolean>(false)
  const isOpenGeoModal = ref<boolean>(false)
  const isOpenEcosystemModal = ref<boolean>(false)

  const isShowTopBanner = computed(() => {
    const path = route.path
    const isProfilePath = ['/', '/students', '/students/ambassadors'].includes(path)

    if (isProfilePath) return !isAuthenticated || (isAuthenticated && hasRole('fiz'))

    return false
  })

  const isShowTopSubscribeBanner = computed(() => {
    const path = route.path

    return (
      [
        '/digital-lecture',
        '/employers-tasks',
        '/research-showcase',
        '/students/ambassadors/employers-tasks',
      ].includes(path) || path.startsWith('/digital-lecture/')
    )
  })

  const openSideMenu = () => {
    isOpenSideMenu.value = true
  }

  const closeSideMenu = () => {
    isOpenSideMenu.value = false
  }

  const openGeoModal = () => {
    isOpenGeoModal.value = true
  }

  const closeGeoModal = () => {
    isOpenGeoModal.value = false
  }

  const openEcosystemModal = () => {
    isOpenEcosystemModal.value = true
  }

  const openChangeRegionModal = () => {
    closeGeoModal()
    openRegionModal()
  }

  await loadRegions()
</script>

<style lang="scss" scoped>
  @use '../assets/scss/layouts/default';

  .default-layout {
    position: relative;

    > .header-container {
      z-index: 60;
      position: sticky;
      top: -110px;

      @include vp-1439 {
        top: -62px;
      }
    }
  }
</style>
